const classlink = {
  title: 'Integrations · ClassLink',
  subtitle: 'OneRoster connections with ClassLink districts',
  new_connection: 'New connection',
  empty: 'There are no ClassLink connections yet. Create the first one with the "New connection" button.',
  loading: 'Loading connections...',
  load_error: 'ClassLink connections could not be loaded.',
  missing_secret: 'Missing secret',

  unavailable: {
    title: 'Feature not available',
    text: 'ClassLink connection management is not available in this environment yet. Try again once the backend is deployed.',
  },

  filters: {
    search: 'Search by name or tenant key',
    status: 'Status',
    status_all: 'All',
    status_enabled: 'Enabled',
    status_disabled: 'Disabled',
    sync: 'Last sync',
    sync_all: 'All',
    sync_never: 'Never synced',
    missing_secret: 'Only with missing secret',
    clear: 'Clear filters',
    no_results: 'No connection matches the applied filters.',
  },

  table: {
    name: 'Name',
    tenant_key: 'Tenant key',
    enabled: 'Enabled',
    schools: 'Schools',
    courses: 'Courses',
    students: 'Students',
    teachers: 'Teachers',
    staff: 'Staff',
    source: 'In ClassLink',
    last_sync: 'Last sync',
    actions: 'Actions',
  },

  // "In ClassLink" column. These are vue-i18n plurals: consumed with `$tc`,
  // not `$t`.
  stats: {
    source_orgs: '{n} organization | {n} organizations',
    source_users: '{n} user | {n} users',
    scoped_hint: 'This connection has a scope configured: it does not include the whole district',
  },

  enabled_switch: {
    on: 'Connection enabled',
    off: 'Connection disabled',
  },

  status: {
    running: 'Running',
    success: 'Succeeded',
    failed: 'Failed',
  },

  relative: {
    just_now: 'moments ago',
    minutes: '{n} min ago',
    hours: '{n} h ago',
    days: '{n} d ago',
  },

  run: {
    never: 'Never synced',
    counts: '{users} users, {courses} courses, {skipped} skipped, {ambiguous} ambiguous, {errors} errors',
  },

  actions: {
    sync: 'Sync',
    sync_incremental: 'Incremental',
    sync_full: 'Full',
    history: 'View history',
    edit: 'Edit connection',
    delete: 'Delete connection',
  },

  sync: {
    started_incremental: 'Incremental sync started',
    started_full: 'Full sync started',
  },

  form: {
    create_title: 'New ClassLink connection',
    edit_title: 'Edit ClassLink connection',
    display_name: 'Name',
    tenant_key: 'Tenant key',
    tenant_key_hint: 'Subdomain of rosterserver.com. Lowercase letters, digits and hyphens only.',
    tenant_key_readonly: 'The tenant key cannot be changed after creation.',
    endpoint_url: 'Endpoint URL',
    endpoint_url_hint: 'Filled in from the tenant key. Change it only if the district uses another domain.',
    client_id: 'Client ID',
    client_secret: 'Client secret',
    client_secret_placeholder: 'Unchanged · ends in {hint}',
    notes: 'Notes',
    test_connection: 'Test connection',
    test_ok: 'Connection succeeded (first organization: {orgName})',
    test_ok_no_org: 'Connection succeeded.',
    test_hint: 'You can save without testing, but testing the connection first is recommended.',
    test_missing_fields: 'Fill in endpoint URL, client ID and client secret to test the connection.',
    required: 'This field is required',
    tenant_key_invalid: 'Only lowercase letters, digits and hyphens are allowed',
    url_invalid: 'Enter a valid https URL',
    cancel: 'Cancel',
    save: 'Save',
    created: 'Connection created',
    updated: 'Connection updated',
  },

  runs: {
    title: 'Sync history',
    subtitle: 'Last {limit} runs of {name}',
    empty: 'There are no runs recorded for this connection yet.',
    load_error: 'The history could not be loaded.',
    close: 'Close',
    col_started_at: 'Started',
    col_mode: 'Mode',
    col_trigger: 'Source',
    col_status: 'Status',
    col_duration: 'Duration',
    col_summary: 'Summary',
    unresolved_title: 'Skipped records: {total}',
    unresolved_group: '{count} in the sample — {reason}',
    mode: {
      incremental: 'Incremental',
      full: 'Full',
    },
    trigger: {
      cron: 'Scheduled',
      manual: 'Manual',
    },
    triggered_by: 'Triggered by {email}',
    in_progress: 'Running',
    duration_seconds: '{n} s',
    detail_title: 'Breakdown by stage',
    col_stage: 'Stage',
    col_outcome: 'Outcome',
    col_count: 'Count',
    errors_title: 'Error sample',
    error_message_title: 'Run error',
    source_totals_title: 'Totals in ClassLink',
    source_totals: '{orgs} organizations · {users} users · {classes} courses · {enrollments} enrollments',
    copy: 'Copy',
    copied: 'Copied to clipboard',
    copy_failed: 'Could not copy. Select the text and copy it manually.',
  },

  stages: {
    district: 'Districts',
    school: 'Schools',
    user: 'Users',
    class: 'Courses',
    enrollment: 'Enrollments',
  },

  outcomes: {
    resolved: 'Resolved',
    linked: 'Linked',
    created: 'Created',
    ambiguous: 'Ambiguous',
    deactivated: 'Deactivated',
    skipped: 'Skipped',
    error: 'Errors',
    notFound: 'Not found',
  },

  outcome: {
    applied: 'Succeeded',
    no_changes: 'No changes',
    needs_attention: 'Needs attention',
  },

  reasons: {
    raw: '{text}',
    ambiguous_candidates: 'There is more than one possible match in BeeReaders (ids: {candidates}). Review it and link the right one manually.',
    unexpected_error: 'Unexpected error: {message}',
    district_unresolved: 'Its district could not be resolved, so this record was not synced.',
    school_unresolved: 'Its school could not be resolved, so this record was not synced.',
    same_roster_as_representative: 'It has the same student roster as another course ({representative}), so they were merged into one.',
    grade_unresolved: 'The grade could not be determined ({kind}).',
    user_unresolved: 'Its user could not be resolved, so the enrollment was not synced.',
    class_unresolved: 'Its course could not be resolved, so the enrollment was not synced.',
  },

  /**
   * Short, parameter-free copy for the drawer's grouped skipped-records view
   * (`unresolvedGroups`): a group bundles the sample of several records, so it
   * is wrong to show one record's params (e.g. the ids in
   * `ambiguous_candidates`) as if they covered the whole group. `reasons`
   * (above) is still used where a single record is shown (the error sample).
   */
  reasons_grouped: {
    raw: 'Reason recorded as free text',
    ambiguous_candidates: 'There is more than one possible match in BeeReaders',
    unexpected_error: 'Unexpected error',
    district_unresolved: 'Its district could not be resolved',
    school_unresolved: 'Its school could not be resolved',
    same_roster_as_representative: 'It has the same student roster as another course',
    grade_unresolved: 'The grade could not be determined',
    user_unresolved: 'Its user could not be resolved',
    class_unresolved: 'Its course could not be resolved',
  },

  delete: {
    title: 'Delete connection',
    confirm: 'You are about to delete the connection {name} ({tenantKey}), which has {users} linked. The connection will stop syncing. Existing users are not deleted.',
    users_count: '{count} users',
    cancel: 'Cancel',
    accept: 'Delete',
    deleted: 'Connection deleted',
  },

  last_attention: 'The last run with problems was {when}',
  mismatch: 'ClassLink reports {users} users and BeeReaders has no synced students',

  reconcile: {
    title: 'Unlinked organizations',
    subtitle: 'Decide which BeeReaders record each one matches',
    stale_source: 'ClassLink did not respond. This is what was left from the last sync and may be outdated.',
    load_error: 'The unlinked organizations could not be loaded.',
    all_linked: 'All organizations have been linked.',
    candidate_plain: '{name}',
    candidate_orphan: '{name} — has no district assigned, that is why the sync does not recognize it',
    candidate_inactive: '{name} — is inactive, that is why the sync does not recognize it',
    no_candidates: 'There is no record with this name in BeeReaders. Create it first: districts in Conglomerates and schools in Schools.',
    link: 'Link',
    linked: 'Organization linked',
    link_error: 'The organization could not be linked.',
    open: 'Review',
  },
  // Scope of the sync (KB-950). See the Spanish file for why `grade_labels`
  // is keyed by code and prefixed with `g`.
  scope: {
    open: 'Sync scope',
    title: 'Sync scope',
    subtitle: 'Choose which schools and which grades are included in every sync of this connection.',
    load_error: 'The sync scope could not be loaded.',
    unavailable: 'The sync scope is not available in this environment yet. Try again once the backend is deployed.',
    stale_source: 'ClassLink did not respond. School names are not available and the selection cannot be changed; what is saved is shown below. The only thing you can do right now is clear the filter.',

    schools_title: 'Schools',
    schools_all: 'This connection has no school filter: it currently syncs the whole district.',
    schools_saved: 'Currently syncing {n} school out of {total}. | Currently syncing {n} schools out of {total}.',
    schools_saved_readonly: 'Currently syncing {n} school. | Currently syncing {n} schools.',
    schools_empty: 'The roster of this connection has no schools.',
    schools_search: 'Search a school by name or identifier',
    schools_no_results: 'No school matches the search.',
    schools_hint: 'If you select none, the filter is cleared and the whole district is synced again.',
    schools_synced_breakdown: '{students} stu. · {teachers} tea. · {staff} staff',
    select_all: 'Select all',
    select_none: 'Clear all',
    selected_count: 'Selected now: {n} of {total}',

    grades_title: 'Grades',
    grades_all: 'This connection has no grade filter: it currently syncs all of them.',
    grades_saved: 'Currently syncing {n} grade. | Currently syncing {n} grades.',
    grades_empty: 'The roster of this connection does not report grades yet. Run a sync once to be able to filter them.',
    grades_hint: 'If you select none, all grades are included.',

    save: 'Save',
    cancel: 'Cancel',
    clear: 'Clear the filter',
    saved: 'Scope updated',
    save_error: 'The scope could not be saved.',
    unavailable_live: 'ClassLink is not responding right now, so a school selection cannot be saved. Try again later.',
    invalid_orgs: 'One of the selected schools no longer exists in ClassLink. Reload the screen and try again.',

    confirm_title: 'Confirm the change',
    confirm_body: 'You are leaving {excluded} school out of the sync; {remaining} will stay in. | You are leaving {excluded} schools out of the sync; {remaining} will stay in.',
    confirm_note: 'Records from the schools and grades left out are deactivated: they stop being visible and stop syncing. Nothing is deleted, and including them again reactivates them.',
    preview_error: 'Could not calculate how many records will be deactivated. You can still save, but without that number.',
    preview_deactivate_title: 'Will be deactivated:',
    preview_users: '{count} student | {count} students',
    preview_courses: '{count} course | {count} courses',
    preview_enrollments: '{count} enrollment | {count} enrollments',
    preview_reactivate: '{count} record that was out of scope will be reactivated. | {count} records that were out of scope will be reactivated.',
    confirm_accept: 'Save anyway',
    confirm_cancel: 'Cancel',

    grade_labels: {
      gBIRTH: 'Birth',
      gPRENATAL: 'Prenatal',
      gIT: 'Infant/Toddler',
      gPR: 'Preschool',
      gPK: 'Pre-Kindergarten',
      gTK: 'Transitional Kindergarten',
      gKG: 'Kindergarten',
      gPS: 'Postsecondary',
      gUG: 'Ungraded',
      gOTHER: 'Other',
      g01: 'Grade 1',
      g02: 'Grade 2',
      g03: 'Grade 3',
      g04: 'Grade 4',
      g05: 'Grade 5',
      g06: 'Grade 6',
      g07: 'Grade 7',
      g08: 'Grade 8',
      g09: 'Grade 9',
      g10: 'Grade 10',
      g11: 'Grade 11',
      g12: 'Grade 12',
      g13: 'Grade 13',
    },
  },
};

export default classlink;
